// src/pages/KPIManagement.js
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Tabs, Tab, Alert
} from '@mui/material';
import {
  TrendingUp, EmojiEvents, Assessment,
  CheckCircle, Warning, Error,
  LocalLaundryService, Build, People
} from '@mui/icons-material';
import { getData } from '../services/storageService';
import { useI18n } from '../i18n/i18n';

const KPIManagement = () => {
  const { t } = useI18n();
  const [kpis, setKpis] = useState([]);
  const [performance, setPerformance] = useState({});
  const [operatorReports, setOperatorReports] = useState([]);
  const [technicianReports, setTechnicianReports] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    loadKPI();
    loadRealData();
  }, []);

  const loadRealData = async () => {
    try {
      const opReports = await getData('operator_reports') || [];
      const techReports = await getData('technician_reports') || [];
      
      setOperatorReports(opReports);
      setTechnicianReports(techReports);
      
      const realPerformance = calculateRealPerformance(opReports, techReports);
      setPerformance(realPerformance);
    } catch (error) {
      console.error('Error loading real data:', error);
    }
  };

  const loadKPI = async () => {
    // Ми завжди беремо дефолтні налаштування, щоб оновити ключі мов, 
    // якщо ви хочете зберігати налаштування користувача, логіку треба трохи змінити,
    // але для перекладу краще поки що брати getDefaultKPIs()
    const defaultKPIs = getDefaultKPIs();
    const storedKPIs = await getData('kpi_settings');
    
    // Якщо дані є в стореджі, але вони старого формату (без nameKey), краще перезаписати їх
    if (storedKPIs && storedKPIs.length > 0 && !storedKPIs[0].nameKey) {
        setKpis(defaultKPIs); 
    } else {
        setKpis(storedKPIs || defaultKPIs);
    }
  };

  // 📊 ОНОВЛЕНІ KPI - ВИКОРИСТОВУЄМО КЛЮЧІ (KEYS) ЗАМІСТЬ ТЕКСТУ
  const getDefaultKPIs = () => [
    // === ТУНЕЛЬ ===
    {
      id: 1,
      category: 'tunnel',
      nameKey: 'kpi_tunnel_production', // Ключ з JSON файлу
      unitKey: 'kpi_unit_kg',           // Ключ з JSON файлу
      targetPerShift: 5000,
      direction: 'higher',
      dataSource: 'operator_reports',
      field: 'tunnelProduction',
      weight: 20,
      icon: <LocalLaundryService />
    },
    {
      id: 2,
      category: 'tunnel',
      nameKey: 'kpi_tunnel_rejects',
      unitKey: 'kpi_unit_kg',
      target: 100,
      direction: 'lower',
      dataSource: 'operator_reports',
      field: 'tunnelRejects',
      weight: 15,
      icon: <Warning />
    },
    {
      id: 3,
      category: 'tunnel',
      nameKey: 'kpi_tunnel_efficiency',
      unitKey: 'kpi_unit_percent',
      target: 80,
      direction: 'higher',
      dataSource: 'calculated',
      weight: 10,
      icon: <TrendingUp />
    },

    // === ПРАЛЬНІ МАШИНИ (СУМОВАНО) ===
    {
      id: 4,
      category: 'washing',
      nameKey: 'kpi_wash_total_cycles',
      unitKey: 'kpi_unit_cycles',
      targetPerShift: 50,
      direction: 'higher',
      dataSource: 'operator_reports',
      field: 'totalWashCycles',
      weight: 15,
      icon: <LocalLaundryService />
    },
    {
      id: 5,
      category: 'washing',
      nameKey: 'kpi_wash_total_production',
      unitKey: 'kpi_unit_kg',
      targetPerShift: 2000,
      direction: 'higher',
      dataSource: 'calculated',
      weight: 12,
      icon: <LocalLaundryService />
    },
    {
      id: 6,
      category: 'washing',
      nameKey: 'kpi_wash_rejects',
      unitKey: 'kpi_unit_kg',
      target: 50,
      direction: 'lower',
      dataSource: 'operator_reports',
      field: 'washRejects',
      weight: 8,
      icon: <Warning />
    },

    // === ПРОДУКТИВНІСТЬ ===
    {
      id: 7,
      category: 'productivity',
      nameKey: 'kpi_kg_per_person_hour',
      unitKey: 'kpi_unit_kg_person_hour',
      target: 50,
      direction: 'higher',
      dataSource: 'calculated',
      weight: 10,
      icon: <People />
    },

    // === ТЕХНІЧНІ ===
    {
      id: 8,
      category: 'technical',
      nameKey: 'kpi_small_parts_production',
      unitKey: 'kpi_unit_pcs',
      targetPerShift: 2000,
      direction: 'higher',
      dataSource: 'technician_reports',
      field: 'smallPartsProduction',
      weight: 8,
      icon: <Build />
    },
    {
      id: 9,
      category: 'technical',
      nameKey: 'kpi_hybrid_machine_output',
      unitKey: 'kpi_unit_pcs',
      targetPerShift: 1500,
      direction: 'higher',
      dataSource: 'technician_reports',
      field: 'hybridMachineOutput',
      weight: 8,
      icon: <Build />
    },
    {
      id: 10,
      category: 'technical',
      nameKey: 'kpi_kannegiesser_total_output',
      unitKey: 'kpi_unit_pcs',
      targetPerShift: 7200,
      direction: 'higher',
      dataSource: 'technician_reports',
      field: 'kannegiesserTotal',
      weight: 12,
      icon: <Build />
    }
  ];

  // 📈 РОЗРАХУНОК РЕАЛЬНОЇ ПРОДУКТИВНОСТІ
  const calculateRealPerformance = (opReports, techReports) => {
    const today = new Date().toISOString().split('T')[0];
    
    const todayOpReports = opReports.filter(report => 
      report.date && report.date.startsWith(today)
    );
    
    const todayTechReports = techReports.filter(report =>
      report.date && report.date.startsWith(today)
    );

    // Розрахунки для тунелю
    const tunnelProduction = todayOpReports.reduce((sum, r) => sum + (r.tunnelProduction || 0), 0);
    const tunnelRejects = todayOpReports.reduce((sum, r) => sum + (r.tunnelRejects || 0), 0);
    const tunnelEfficiency = Math.min(100, (tunnelProduction / 5000) * 100);

    // Розрахунки для пральних машин (СУМОВАНО)
    const wash20kgCycles = todayOpReports.reduce((sum, r) => sum + (r.washCycles20kg || 0), 0);
    const wash40kgCycles = todayOpReports.reduce((sum, r) => sum + (r.washCycles40kg || 0), 0);
    const wash60kgCycles = todayOpReports.reduce((sum, r) => sum + (r.washCycles60kg || 0), 0);
    
    const totalWashCycles = wash20kgCycles + wash40kgCycles + wash60kgCycles;
    const totalWashProduction = (wash20kgCycles * 20) + (wash40kgCycles * 40) + (wash60kgCycles * 55);
    const washRejects = todayOpReports.reduce((sum, r) => sum + (r.washRejects || 0), 0);

    // Загальна продуктивність
    const totalProduction = tunnelProduction + totalWashProduction;
    const kgPerPersonHour = Math.round(totalProduction / (8 * 25));

    // Технічні показники
    const smallPartsProduction = todayTechReports.reduce((sum, r) => sum + (r.smallPartsProduction || 0), 0);
    const hybridMachineOutput = todayTechReports.reduce((sum, r) => sum + (r.hybridMachineOutput || 0), 0);
    const kannegiesserTotal = todayTechReports.reduce((sum, r) => 
      sum + (r.kannegiesser1Output || 0) + (r.kannegiesser2Output || 0) + 
      (r.kannegiesser3Output || 0) + (r.kannegiesser4Output || 0), 0
    );

    return {
      // Туннель
      '1': { actual: tunnelProduction, trend: 0 },
      '2': { actual: tunnelRejects, trend: 0 },
      '3': { actual: Math.round(tunnelEfficiency), trend: 0 },
      
      // Пральні машини
      '4': { actual: totalWashCycles, trend: 0 },
      '5': { actual: totalWashProduction, trend: 0 },
      '6': { actual: washRejects, trend: 0 },
      
      // Продуктивність
      '7': { actual: kgPerPersonHour, trend: 0 },
      
      // Технічні
      '8': { actual: smallPartsProduction, trend: 0 },
      '9': { actual: hybridMachineOutput, trend: 0 },
      '10': { actual: kannegiesserTotal, trend: 0 }
    };
  };

  // 🧮 РОЗРАХУНОК БОНУСІВ
  const calculateBonus = (kpiId, actual) => {
    const kpi = kpis.find(k => k.id === kpiId);
    if (!kpi) return 0;
    
    const target = kpi.targetPerShift || kpi.target;
    const percentage = (actual / target) * 100;
    
    if (kpi.direction === 'lower') {
      if (actual <= target) return kpi.weight;
      if (actual <= target * 1.1) return kpi.weight * 0.5;
      if (actual <= target * 1.2) return kpi.weight * 0.25;
      return 0;
    } else {
      if (percentage >= 100) return kpi.weight;
      if (percentage >= 90) return kpi.weight * 0.5;
      if (percentage >= 80) return kpi.weight * 0.25;
      return 0;
    }
  };

  const totalBonus = kpis.reduce((sum, kpi) => {
    const perf = performance[kpi.id];
    return sum + (perf ? calculateBonus(kpi.id, perf.actual) : 0);
  }, 0);

  // 📊 КАТЕГОРІЇ ДЛЯ ФІЛЬТРУ (динамічний переклад)
  const categories = [
    { key: 'all', name: t('kpi_tab_all'), icon: <Assessment /> },
    { key: 'tunnel', name: t('kpi_category_tunnel'), icon: <LocalLaundryService /> },
    { key: 'washing', name: t('kpi_category_washing'), icon: <LocalLaundryService /> },
    { key: 'productivity', name: t('kpi_category_productivity'), icon: <People /> },
    { key: 'technical', name: t('kpi_category_technical'), icon: <Build /> }
  ];

  const filteredKpis = kpis.filter(kpi => 
    activeCategory === 'all' || kpi.category === activeCategory
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('kpi_title')}
      </Typography>

      {/* Інформація про дані */}
      <Alert severity="info" sx={{ mb: 3 }}>
        {t('kpi_data_source_info')}: {operatorReports.length} {t('kpi_operator_reports')}, {technicianReports.length} {t('kpi_technician_reports')}
      </Alert>

      {/* Загальний бонус */}
      <Card sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'success.light' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="success.main" sx={{ fontWeight: 700, mb: 1 }}>
            {Math.round(totalBonus)}% {t('kpi_bonus_achieved')}
          </Typography>
          <Typography variant="body1" color="success.dark">
            {t('kpi_bonus_description')}
          </Typography>
        </Box>
      </Card>

      {/* Фільтр по категоріях */}
      <Tabs 
        value={activeCategory} 
        onChange={(e, newValue) => setActiveCategory(newValue)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {categories.map((category) => (
          <Tab 
            key={category.key}
            value={category.key}
            icon={category.icon}
            label={category.name}
          />
        ))}
      </Tabs>

      {/* ТАБЛИЦЯ KPI */}
      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>{t('kpi_metric')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>{t('kpi_target')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>{t('kpi_actual')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>{t('kpi_performance')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>{t('kpi_bonus')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>{t('kpi_status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredKpis.map((kpi) => {
              const perf = performance[kpi.id];
              const actual = perf?.actual || 0;
              const target = kpi.targetPerShift || kpi.target;
              const percentage = kpi.direction === 'lower' 
                ? Math.max(0, 100 - ((actual - target) / target) * 100)
                : (actual / target) * 100;
              const bonus = calculateBonus(kpi.id, actual);
              
              return (
                <TableRow key={kpi.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: 'primary.main' }}>
                        {kpi.icon}
                      </Box>
                      <Box>
                        <Typography fontWeight="500">
                          {/* ТУТ ГОЛОВНА ЗМІНА: ВИКОРИСТОВУЄМО КЛЮЧ ДЛЯ ПЕРЕКЛАДУ */}
                          {t(kpi.nameKey) || kpi.name} 
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('kpi_weight')}: {kpi.weight}%
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {target} {t(kpi.unitKey)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="500">
                      {actual} {t(kpi.unitKey)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min(Math.max(percentage, 0), 100)}
                        color={
                          percentage >= 100 ? 'success' :
                          percentage >= 80 ? 'warning' : 'error'
                        }
                        sx={{ 
                          flex: 1, 
                          height: 8, 
                          borderRadius: 4 
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 40 }}>
                        {Math.round(percentage)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={`${bonus}%`}
                      color={bonus > 0 ? 'success' : 'default'}
                      variant={bonus > 0 ? 'filled' : 'outlined'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      icon={percentage >= 100 ? <CheckCircle /> : percentage >= 80 ? <Warning /> : <Error />}
                      label={
                        percentage >= 100 ? t('kpi_excellent') :
                        percentage >= 80 ? t('kpi_good') : t('kpi_needs_improvement')
                      }
                      color={
                        percentage >= 100 ? 'success' :
                        percentage >= 80 ? 'warning' : 'error'
                      }
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Повідомлення якщо немає даних */}
      {filteredKpis.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {t('kpi_no_data')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default KPIManagement;